import { useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle } from 'lucide-react';

/**
 * Permanent Delete requires a two-step confirmation:
 *   Step 1 — acknowledge the consequences.
 *   Step 2 — type the word DELETE to enable the submit button.
 * Accounts / Design Ops go through the same UX; server queues the pending
 * action so an Owner still gets the final say.
 */
export function BulkPermanentDeleteDialog({ module, moduleLabel, ids, needsApproval, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (confirm.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/bulk/${module}`, { ids, action: 'delete_permanent', payload: {} });
      if (r.data.committed) {
        toast.success(`${moduleLabel}: ${r.data.summary.applied_count} permanently deleted`);
      } else {
        toast.success(`Permanent-delete request sent to the Owner (${r.data.target_count} row${r.data.target_count > 1 ? 's' : ''})`);
      }
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[480px]" data-testid={`bulk-delete-dialog-${module}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[hsl(var(--destructive))]">
            <AlertTriangle size={16} /> Permanently delete {ids.length} {moduleLabel}
          </DialogTitle>
          <DialogDescription>
            This is irreversible. The rows will be removed from the database entirely.
            Consider <strong>Archive</strong> instead — it hides rows without losing data.
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-3 text-[13px]">
            <div className="rounded-md p-3 bg-[hsl(var(--destructive)/.08)] border" style={{ borderColor: 'hsl(var(--destructive)/.35)' }}>
              <div className="font-semibold text-[hsl(var(--destructive))] mb-1">You are about to delete forever</div>
              <ul className="list-disc pl-4 text-[12.5px] text-[hsl(var(--foreground-muted))] space-y-0.5">
                <li>{ids.length} {moduleLabel.toLowerCase()} record{ids.length === 1 ? '' : 's'}</li>
                <li>Any linked history for these rows will be orphaned</li>
                <li>{needsApproval ? 'The Owner still needs to approve before it is applied.' : 'The action commits immediately once you confirm.'}</li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button variant="destructive" onClick={() => setStep(2)} data-testid={`bulk-delete-continue-${module}`}>
                I understand — continue
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))]">
                Type DELETE to confirm
              </label>
              <Input
                autoFocus value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="DELETE"
                data-testid={`bulk-delete-confirm-input-${module}`}
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)} disabled={busy}>Back</Button>
              <Button variant="destructive" onClick={submit}
                      disabled={busy || confirm.trim().toUpperCase() !== 'DELETE'}
                      data-testid={`bulk-delete-submit-${module}`}>
                {busy ? 'Deleting…' : (needsApproval ? 'Request deletion' : 'Delete forever')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
